import moment from "moment/moment";
import React, { useEffect, useRef, useState } from "react";

import dashboardStatIconWhite from "../../../assets/img/icons/dashboard-stat-icon-white.svg";
import dashboardStatIconHover from "../../../assets/img/icons/dashboradStatIconHover.svg";
import {
  AttendanceDonutWrapper,
  AttendanceLegendDot,
  AttendanceLegendLabel,
  AttendanceLegendList,
  AttendanceLegendRow,
  AttendanceLegendValue,
  TodaysAttendanceChartContainer,
  TodaysAttendanceContainer,
  TodaysAttendanceHeader,
  TodaysAttendanceIconButton,
  TodaysAttendanceSkeletonCircle,
  TodaysAttendanceSkeletonIcon,
  TodaysAttendanceSkeletonLegend,
  TodaysAttendanceSkeletonTitle,
  TodaysAttendanceTitle,
} from "../../DashBoardNew/dashBoardNewStyles";
import ButtonHelpTooltip from "components/Tooltip/ButtonHelpTooltip";

const VIEWBOX = 164;
const DONUT_CENTER = VIEWBOX / 2;
// Layered donut: absent inner 38, outer ~76.5. Present sits larger on both sides — inner 36, outer 82 (past absent on inside + outside).
const ABSENT_RING_RADIUS = 57.25;
const ABSENT_STROKE_WIDTH = 38.5;
const PRESENT_RING_RADIUS = 57;
const PRESENT_STROKE_WIDTH = 44;
const HOLE_RADIUS = 34;
const PRESENT_CIRCUMFERENCE = 2 * Math.PI * PRESENT_RING_RADIUS;

let todaysAttendanceGradientSeq = 0;

// Figma Ellipse 11643 linear gradient (handles → userSpaceOnUse in 164×164 viewBox)
const PRESENT_GRADIENT = {
  x1: 0.25304876129346227 * VIEWBOX,
  y1: 0.2195122731752548 * VIEWBOX,
  x2: 0.8658542291237079 * VIEWBOX,
  y2: 0.8109757393089271 * VIEWBOX,
};



const TodaysAttendance = ({
  attendanceData,
  getDashboardAttendance,
  attendanceIsLoading,
  selectedOrganization,
  history,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const gradientIdRef = useRef(null);
  if (gradientIdRef.current === null) {
    todaysAttendanceGradientSeq += 1;
    gradientIdRef.current = `presentGradient-${todaysAttendanceGradientSeq}`;
  }
  const presentGradientId = gradientIdRef.current;

  // Fetch attendance data on component mount or when organization changes
  useEffect(() => {
    if (selectedOrganization?.id && getDashboardAttendance) {
      const startTime = moment().format("YYYY-MM-DD");
      const endTime = moment().format("YYYY-MM-DD");

      const payload = {
        organization_id: selectedOrganization.id,
        start_time: startTime,
        end_time: endTime,
      };
      getDashboardAttendance(payload);
    }
  }, [selectedOrganization, getDashboardAttendance]);

  const safeAttendanceData = attendanceData || {
    total_members: 0,
    present: 0,
    late: 0,
    absent: 0,
  };

  const { total_members, present, absent } = safeAttendanceData;
  const presentPercentage =
    total_members > 0 ? Math.round((present / total_members) * 100) : 0;
  const presentOffset =
    PRESENT_CIRCUMFERENCE * (1 - presentPercentage / 100);

  if (attendanceIsLoading) {
    return (
      <TodaysAttendanceContainer>
        <TodaysAttendanceHeader>
          <TodaysAttendanceSkeletonTitle />
          <TodaysAttendanceSkeletonIcon />
        </TodaysAttendanceHeader>
        <TodaysAttendanceChartContainer>
          <TodaysAttendanceSkeletonCircle
            style={{ width: "164px", height: "164px", position: "static", flexShrink: 0 }}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: "9px", flex: 1 }}>
            <TodaysAttendanceSkeletonLegend style={{ position: "static", width: "100%" }} />
            <TodaysAttendanceSkeletonLegend style={{ position: "static", width: "100%" }} />
            <TodaysAttendanceSkeletonLegend style={{ position: "static", width: "100%" }} />
          </div>
        </TodaysAttendanceChartContainer>
      </TodaysAttendanceContainer>
    );
  }

  return (
    <ButtonHelpTooltip
      transform="translateX(-50%) translateY(100%)"
      enabled={true}
      text="Total number of people in the organization and how many of them are online and offline."
    >
      <TodaysAttendanceContainer>
        <TodaysAttendanceHeader>
          <TodaysAttendanceTitle>Todays attendance</TodaysAttendanceTitle>
          <TodaysAttendanceIconButton
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <img
              src={isHovered ? dashboardStatIconHover : dashboardStatIconWhite}
              alt="Attendance icon"
              onClick={() => history.push(`/user/attendance`)}
            />
          </TodaysAttendanceIconButton>
        </TodaysAttendanceHeader>

        <TodaysAttendanceChartContainer>
          {/* Donut chart */}
          <AttendanceDonutWrapper>
            <svg
              viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
              width="100%"
              height="100%"
              aria-label={`Attendance: ${presentPercentage}% present`}
            >
              <title>{`Attendance: ${presentPercentage}% present`}</title>
              <defs>
                <linearGradient
                  id={presentGradientId}
                  x1={PRESENT_GRADIENT.x1}
                  y1={PRESENT_GRADIENT.y1}
                  x2={PRESENT_GRADIENT.x2}
                  y2={PRESENT_GRADIENT.y2}
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0%" stopColor="#aa8ef6" />
                  <stop offset="100%" stopColor="#6c2ef2" />
                </linearGradient>
              </defs>

              {/* Absent — smaller inner ring (Figma Ellipse 11642 ~153px), full pink track */}
              <circle
                cx={DONUT_CENTER}
                cy={DONUT_CENTER}
                r={ABSENT_RING_RADIUS}
                fill="none"
                stroke="#fda4af"
                strokeWidth={ABSENT_STROKE_WIDTH}
              />

              {/* Present — larger outer ring (Figma Ellipse 11643 164px) on top, gradient arc */}
              <circle
                cx={DONUT_CENTER}
                cy={DONUT_CENTER}
                r={PRESENT_RING_RADIUS}
                fill="none"
                stroke={`url(#${presentGradientId})`}
                strokeWidth={PRESENT_STROKE_WIDTH}
                strokeDasharray={PRESENT_CIRCUMFERENCE}
                strokeDashoffset={presentOffset}
                strokeLinecap="butt"
                transform={`rotate(-90 ${DONUT_CENTER} ${DONUT_CENTER})`}
              />

              <circle
                cx={DONUT_CENTER}
                cy={DONUT_CENTER}
                r={HOLE_RADIUS}
                fill="#ffffff"
              />

              {/* Figma text “65%”: Mona Sans Bold 16, #6c2ef2 */}
              <text
                x={DONUT_CENTER}
                y={DONUT_CENTER}
                textAnchor="middle"
                dominantBaseline="central"
                fill="#6c2ef2"
                fontWeight="900"
                fontSize="16"
              >
                {presentPercentage}%
              </text>
            </svg>
          </AttendanceDonutWrapper>

          {/* Legend */}
          <AttendanceLegendList>
            {/* Total — no colour dot per Figma spec */}
            <AttendanceLegendRow>
              <AttendanceLegendLabel>Total</AttendanceLegendLabel>
              <AttendanceLegendValue>{total_members}</AttendanceLegendValue>
            </AttendanceLegendRow>

            {/* Present */}
            <AttendanceLegendRow>
              <AttendanceLegendDot color="#6c2ef2" />
              <AttendanceLegendLabel>Present</AttendanceLegendLabel>
              <AttendanceLegendValue>{present}</AttendanceLegendValue>
            </AttendanceLegendRow>

            {/* Absent */}
            <AttendanceLegendRow>
              <AttendanceLegendDot color="#fda4af" />
              <AttendanceLegendLabel>Absent</AttendanceLegendLabel>
              <AttendanceLegendValue>{absent}</AttendanceLegendValue>
            </AttendanceLegendRow>
          </AttendanceLegendList>
        </TodaysAttendanceChartContainer>
      </TodaysAttendanceContainer>
    </ButtonHelpTooltip>
  );
};

export default TodaysAttendance;